/*
================================================================================
|                                                                              |
|  FILE: src/App.js                                                            |
|  PURPOSE: Main app component. Handles routing and global layout.             |
|  NOTE: This version contains the corrected routing logic and lazy loading.   |
|                                                                              |
================================================================================
*/
import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import ScrollToTop from "./components/common/ScrollToTop";
import EcoRevivePage from "./pages/EcoRevivePage"; // Direct import for now
import { initGA, trackPageView } from "./utils/analytics";
import usePageTracking from "./hooks/usePageTracking";

// Lazy load pages for better performance
const HomePage = React.lazy(() => import("./pages/HomePage"));
const AboutPage = React.lazy(() => import("./pages/AboutPage"));
const ServicesPage = React.lazy(() => import("./pages/ServicesPage"));
const MiningPage = React.lazy(() => import("./pages/MiningPage"));
const OilGasPage = React.lazy(() => import("./pages/OilGasPage"));
const EnvironmentalPage = React.lazy(() => import("./pages/EnvironmentalPage"));
const ImpactPage = React.lazy(() => import("./pages/ImpactPage"));
const NewsPage = React.lazy(() => import("./pages/NewsPage"));
const NewsArticlePage = React.lazy(() => import("./pages/NewsArticlePage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600"></div>
  </div>
);

// Component to handle page tracking
const PageTracker = () => {
  usePageTracking();
  return null;
};

export default function App() {
  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
    trackPageView('Milliardi Energo - Home', window.location.pathname);
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <PageTracker />
        <ScrollToTop />
        <Navbar />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/mining" element={<MiningPage />} />
            <Route path="/services/oil-gas" element={<OilGasPage />} />
            <Route path="/services/environmental" element={<EnvironmentalPage />} />
            <Route path="/impact" element={<ImpactPage />} />
            <Route path="/impact/ecorevive" element={<EcoRevivePage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsArticlePage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </Suspense>
        <Footer />
      </Router>
    </HelmetProvider>
  );
}